import { Drawer as MuiDrawer, styled, Box } from '@mui/material';
import type { DrawerProps as MuiDrawerProps, SxProps } from '@mui/material';
import {
  X,
  PencilSimple,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
  ArrowsOutSimple,
} from '@phosphor-icons/react';
import { TextInput, TextInputProps } from 'components/inputs';
import { useKeyPress } from 'hooks/useKeyPress';
import React, { useMemo } from 'react';
import { colors } from 'styles/theme/colors';
import { palette } from 'styles/theme/palette';
import { Text, IconButton } from 'components';

export type DrawerProps = Pick<
  MuiDrawerProps,
  'hideBackdrop' | 'open' | 'classes' | 'sx' | 'anchor'
> & {
  onClose?: (
    event: React.SyntheticEvent | KeyboardEvent,
    reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick'
  ) => void;
  headerSlot?: React.ReactNode;
  imageHeader?: {
    text: React.ReactNode;
    subText: React.ReactNode;
    imageUrl?: string;
    ImageIconSize?: number;
  };
  children: React.ReactNode;
  footerSlot?: React.ReactNode;
  headerSx?: SxProps;
  bodySx?: SxProps;
  footerSx?: SxProps;
  variant?: 'temporary' | 'persistent' | 'permanent';
  handleSaveClick?: () => void;
  handleEditClick?: React.MouseEventHandler<SVGSVGElement>;
  handleCancelClick?: React.MouseEventHandler<SVGSVGElement>;
  showEdit?: boolean;
  value?: string;
  onChange?: TextInputProps['onChange'];
  closeEscape?: boolean;
  onFullViewClick?: () => void;
  'data-testid'?: string;
};

/**
 * Drawer component, part of Delogue design system.
 * @param open - whether the drawer is open
 * @param hideBackdrop - whether to hide the backdrop when the drawer is open
 * @param headerSlot - content to be displayed in the header of the drawer
 * @param children - content to be displayed in the body of the drawer
 * @param footerSlot - content to be displayed in the footer of the drawer
 * @param onClose - callback function to be called when the drawer is closed
 * @param imageHeader - object containing text, subText, and optional imageUrl for the header, headerSlot will be ignored if this is provided
 * @param handleEditClick - callback function to be called when the edit icon is clicked, also decides if the headerSlot is editable
 * @param showEdit - whether to show input field and edit icons in the header, if true headerSlot will be ignored
 * @param handleSaveClick - callback function to be called when the save button is clicked
 * @param handleCancelClick - callback function to be called when the cancel icon is clicked
 * @param value - value of the input field in the header when showEdit is true
 * @param onChange - callback function to be called when the input field value changes
 * @param closeEscape - whether to close the drawer when the Escape key is pressed, default is true
 * @param anchor - the side from which the drawer will slide in, default is 'right'
 * @param onFullViewClick - callback function to be called when the full view icon is clicked. If provided, the expand icon will be shown in the header.
 */
export const Drawer = ({
  onClose,
  hideBackdrop = false,
  open,
  anchor = 'right',
  headerSlot,
  headerSx,
  imageHeader,
  footerSlot,
  footerSx,
  children,
  bodySx,
  sx: sxProp,
  variant,
  handleSaveClick,
  handleCancelClick,
  handleEditClick,
  showEdit,
  value,
  onChange,
  closeEscape,
  onFullViewClick,
  'data-testid': dataTestId = 'drawer',
  ...rest
}: DrawerProps) => {
  const sx = useMemo(
    () => ({
      width: 'clamp(280px, 50vw, 632px)',
      background: palette.bg.primary,
      padding: '24px',
      borderLeft: `0.5px solid ${palette.utility.grey[100]}`,
      boxShadow: '0px 4px 16px 0px rgba(34, 34, 34, 0.08)',
      ...sxProp,
    }),
    [sxProp]
  );

  useKeyPress('Escape', event => {
    if (closeEscape && open) {
      onClose?.(event, 'escapeKeyDown');
    }
  });

  return (
    <MuiDrawer
      data-testid={dataTestId}
      onClose={onClose}
      hideBackdrop={hideBackdrop}
      open={open}
      keepMounted
      slotProps={{ paper: { sx } }}
      anchor={anchor}
      variant={variant}
      sx={{
        zIndex: 1300,
        '& .MuiModal-backdrop': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <Header data-testid={`${dataTestId}_header`} sx={headerSx} imageHeader={Boolean(imageHeader)}>
        {showEdit ? (
          <Row sx={{ gap: '10px' }}>
            <Column>
              <TextInput
                data-testid={`${dataTestId}_header_input`}
                aria-label="note-text"
                onChange={onChange}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSaveClick?.();
                  }
                }}
                value={value}
                autoFocus
                fullWidth
              />
            </Column>
            <Row sx={{ gap: '8px' }}>
              <XCircle
                data-testid={`${dataTestId}_header_x_circle`}
                onClick={handleCancelClick}
                color={colors.grey[300]}
                aria-label="clear-text"
                size={16}
                style={{ height: '24px', width: '24px', cursor: 'pointer' }}
              />
              <CheckCircle
                data-testid={`${dataTestId}_header_check_circle`}
                aria-label="save-note"
                size={16}
                style={{ height: '24px', width: '24px', cursor: 'pointer' }}
                color={colors.success[500]}
                onClick={handleSaveClick}
              />
            </Row>
          </Row>
        ) : (
          <Row>
            {imageHeader ? (
              <>
                {imageHeader.imageUrl ? (
                  <Image data-testid={`${dataTestId}_header_image`} src={imageHeader.imageUrl} />
                ) : (
                  <EmptyImage data-testid={`${dataTestId}_header_empty_image`}>
                    <ImageIcon size={imageHeader?.ImageIconSize ?? 18} />
                  </EmptyImage>
                )}
                <SubText>
                  <HeaderText
                    dataTestId={dataTestId}
                    text={imageHeader.text}
                    handleEditClick={handleEditClick}
                  />
                  <Text color="placeholderSubtle">{imageHeader.subText}</Text>
                </SubText>
              </>
            ) : (
              <HeaderText
                dataTestId={dataTestId}
                text={headerSlot}
                handleEditClick={handleEditClick}
              />
            )}
          </Row>
        )}
        <Close imageHeader={Boolean(imageHeader)}>
          {onFullViewClick && (
            <IconButton
              size="md"
              data-testid={`${dataTestId}_header_full_view_button`}
              onClick={onFullViewClick}
            >
              <ArrowsOutSimple size={24} />
            </IconButton>
          )}
          <IconButton
            size="md"
            data-testid={`${dataTestId}_header_close_button`}
            onClick={e => onClose && onClose(e, 'closeClick')}
          >
            <X size={24} />
          </IconButton>
        </Close>
      </Header>
      <Body data-testid={`${dataTestId}_body`} sx={bodySx}>
        {children}
      </Body>
      <Footer data-testid={`${dataTestId}_footer`} sx={footerSx}>
        {footerSlot}
      </Footer>
    </MuiDrawer>
  );
};

type HeaderTextProps = {
  handleEditClick?: React.MouseEventHandler<SVGSVGElement>;
  text: React.ReactNode;
  dataTestId?: string;
};

const HeaderText = ({ handleEditClick, text, dataTestId = 'drawer' }: HeaderTextProps) => {
  return (
    <>
      <Text variant="headlineMd" weight="semiBold" data-testid={`${dataTestId}_header_text`}>
        {text}
      </Text>
      {handleEditClick && (
        <HeaderPencil data-testid={`${dataTestId}_header_edit_button`}>
          <PencilSimple size={24} onClick={handleEditClick} />
        </HeaderPencil>
      )}
    </>
  );
};

const Header = styled('header')<{ imageHeader?: boolean }>(({ theme, imageHeader }) => ({
  display: 'flex',
  marginBottom: theme.spacing(5),
  marginTop: 0,
  justifyContent: 'space-between',
}));

const Image = styled('img')({
  width: '72px',
  height: '72px',
  borderRadius: '4px',
});

const EmptyImage = styled('div')(({ theme }) => ({
  width: '72px',
  height: '72px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.utility.grey[100],
}));

const SubText = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: theme.spacing(6),
}));

const Footer = styled('footer')({
  width: '100%',
});

const Body = styled('section')({
  height: '100%',
});

const Row = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const Column = styled(Box)`
  display: flex;
  flex-direction: column;
  margin: 4px;
  padding: 4px;
`;
const HeaderPencil = styled(Box)`
  margin-right: 4px;
  margin-left: 4px;
  padding: 4px;
  cursor: pointer;
`;

const Close = styled('div')<{ imageHeader?: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-top: ${p => (p.imageHeader ? '12px' : '6px')};
  margin-right: ${p => (p.imageHeader ? '12px' : '6px')};
  height: 24px;
  button {
    padding: 0;
  }
`;
